// Identity-scoped browser storage (issue #301, security expert item 9).
//
// Drafts and AI-disclosure acknowledgements are written under keys that do not
// name the user or tenant, so on a shared browser the next identity would
// inherit them (a draft pipeline, or an AI-disclosure ack that the new user
// never gave — a compliance concern). resetClientState() sweeps these on every
// identity/tenant change.
//
// This is the single registry. A feature that stores identity-scoped data
// under an un-namespaced key adds its prefix here.
//
// The two keys #301 left as follow-ups are now here (#302 item 4): the
// catalog copilot sidebar flag and the Slack OAuth CSRF state. The Slack
// entry's comment records why sweeping it is NOT the protection it was
// originally claimed to be, and where the real check lives.

const LOCAL_PREFIXES = [
  'vharta-copilot-sidebar-open', // catalog copilot open/closed, per user (#302 item 4)
  'vharta-ai-disclosure-dismissed',
  'vharta:disclosure:',
  'vharta-ai-exec-disclosure-dismissed:',
  'ai-assist-dismissed-',
  'vharta:stategraph:run-initial-state:',
  'vharta-ca-trust-ack-', // CA-trust acknowledgement (compliance): per user, per cluster
  'vharta-byok-cost-ack-dismissed-',
  'vharta.legacy_chat_trigger_dismissed.',
  'vharta.runs.savedViews',
  'vharta:engine-cluster:', // ClusterEngineSelector's remembered cluster ids (tenant data)
  'vharta-locale-ccy-banner-dismissed-',
  'vharta-tour-pages-visited',
  'vharta-tour-completed',
  'vharta-tour-cooldowns',
  'vharta_tour_progress',
]

const SESSION_PREFIXES = [
  'vharta-ai-disclosure-acked-sessions',
  'pipeline-editor-state',
  // Slack OAuth CSRF nonce. Kept in the sweep, but it is NOT the protection —
  // PR #596's comment claimed it was, and that was wrong.
  //
  // sessionStorage is PER-TAB and this sweep runs only in the tab whose
  // identity changed (resetClientState). The tab doing a Slack connect is away
  // at Slack with no JS running, and the cross-tab handler calls
  // resetQueryState() without sweeping. So the scenario #596 described —
  // another tab switching tenant mid-flow — left this nonce in place, and the
  // callback could still bind a workspace to the wrong tenant.
  //
  // The real fix is in the flow itself: the nonce now carries {state, tenantId,
  // userId} and ChannelWizard refuses the callback when the identity no longer
  // matches. This entry stays as defence in depth for the same-tab case (a
  // login or logout in this tab between writing the nonce and redirecting).
  'vharta_slack_oauth_state',
]

function sweep(storage: Storage, prefixes: string[]): void {
  const doomed: string[] = []
  for (let i = 0; i < storage.length; i++) {
    const key = storage.key(i)
    if (key && prefixes.some((p) => key.startsWith(p))) doomed.push(key)
  }
  doomed.forEach((k) => storage.removeItem(k))
}

export function sweepIdentityScopedStorage(): void {
  try {
    sweep(localStorage, LOCAL_PREFIXES)
    sweep(sessionStorage, SESSION_PREFIXES)
  } catch {
    // Storage can throw (private mode / blocked); nothing to sweep then.
  }
}
